/**
 * OnboardingSlideshow.js
 *
 * A modal carousel shown after login when the coach's onboarding
 * steps are not yet complete. Displays 6 slides walking new coaches
 * through profile setup, program creation, Stripe, and listing.
 *
 * Brand: Viveka Coach (purple palette)
 * Fonts: Plus Jakarta Sans + Inter (loaded in public/index.html)
 *
 * Integration:
 *   Rendered by PageWrapper.js when:
 *     1. The user is logged in
 *     2. The user is a provider (coach)
 *     3. currentUser.attributes.profile.protectedData.onboardingComplete !== true
 *
 *   On "Get Started" or close, dispatches markOnboardingViewed()
 *   which sets onboardingComplete = true via the Sharetribe SDK.
 */

import React, { useState, useEffect, useCallback } from 'react';
import css from './OnboardingSlideshow.css';

// ── Slide Content ────────────────────────────

const SLIDES = [
  {
    id: 'welcome',
    badge: 'Welcome',
    title: 'Welcome to V-Coach',
    subtitle: null, // uses custom "by Viveka." rendering
    body:
      "Your coaching infrastructure is ready. Over the next few steps we'll help you set up everything you need to manage clients, run sessions, and grow your practice — all in one place.",
    tip: null,
    icon: 'sphere',
    progress: null,
  },
  {
    id: 'profile',
    badge: 'Step 1 of 5',
    title: 'Complete Your Profile',
    subtitle: 'Make a great first impression.',
    body:
      'Add your photo, write a compelling bio, and include your social handles and location. Clients browse coaches by specialty and location — a polished profile helps you stand out.',
    tip: 'Tip: Use a high-resolution headshot and highlight your coaching certifications and areas of expertise.',
    icon: 'profile',
    progress: 20,
  },
  {
    id: 'program',
    badge: 'Step 2 of 5',
    title: 'Create Your Program',
    subtitle: 'Structure builds trust.',
    body:
      'Define your coaching program with clear goals, session descriptions, and preparation notes. A structured program demonstrates consistent quality and sets concrete client expectations.',
    tip: 'Tip: Start with one signature program — you can always add more later.',
    icon: 'program',
    progress: 40,
  },
  {
    id: 'stripe',
    badge: 'Step 3 of 5',
    title: 'Connect Payments',
    subtitle: 'Get paid seamlessly.',
    body:
      'Link your Stripe account so you can accept payments directly through the platform. Viveka handles invoicing and payouts so you can focus on coaching.',
    tip: "Tip: Have your bank details ready — Stripe setup takes about 5 minutes.",
    icon: 'stripe',
    progress: 60,
  },
  {
    id: 'listing',
    badge: 'Step 4 of 5',
    title: 'Create Your Listing',
    subtitle: 'Be discovered.',
    body:
      'Your ad listing is what clients see when they search the marketplace. Connect it to your program, set your pricing, and describe the transformation you deliver.',
    tip: 'Tip: Clear pricing and a strong value proposition convert more browsers into clients.',
    icon: 'listing',
    progress: 80,
  },
  {
    id: 'ready',
    badge: 'Step 5 of 5',
    title: "You're All Set!",
    subtitle: 'Your coaching practice is live.',
    body:
      'Your dashboard, automated notifications, session notes, and accountability tracker are all ready to go. Clients can now find you, book sessions, and start their journey.',
    tip: null,
    icon: 'rocket',
    progress: 100,
  },
];

// ── V-Sphere SVG (Coach gradient) ────────────

const VSphereSVG = ({ size = 72 }) => (
  <svg width={size} height={size} viewBox="0 0 120 120" fill="none">
    <defs>
      <radialGradient id="onb-coachGrad" cx="78%" cy="22%" r="65%">
        <stop offset="0%" stopColor="#E879F9" />
        <stop offset="50%" stopColor="#A855F7" />
        <stop offset="100%" stopColor="#581C87" />
      </radialGradient>
    </defs>
    <circle cx="60" cy="60" r="56" fill="url(#onb-coachGrad)" />
    <path d="M26,26 L43,30 L58,78 L77,30 L96,24 L74,99 L48,99 Z" fill="white" />
  </svg>
);

// ── Slide Icons ──────────────────────────────

const ICON_PATHS = {
  profile: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 1 0 -16 0" />
    </>
  ),
  program: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M8 7h8" />
      <path d="M8 11h8" />
      <path d="M8 15h4" />
    </>
  ),
  stripe: (
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
      <path d="M6 15h4" />
    </>
  ),
  listing: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
      <path d="M8 11h6" />
      <path d="M11 8v6" />
    </>
  ),
  rocket: (
    <>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </>
  ),
};

const SlideIcon = ({ type }) => {
  if (type === 'sphere') {
    return (
      <div className={css.sphereIcon}>
        <VSphereSVG size={72} />
      </div>
    );
  }

  return (
    <div className={css.slideIcon}>
      <svg
        width={28}
        height={28}
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {ICON_PATHS[type]}
      </svg>
    </div>
  );
};

// ── Dot Indicators ───────────────────────────

const DotIndicators = ({ total, current, onDotClick }) => (
  <div className={css.dots}>
    {Array.from({ length: total }).map((_, i) => (
      <button
        key={i}
        onClick={() => onDotClick(i)}
        className={`${css.dot} ${i === current ? css.dotActive : ''}`}
        aria-label={`Go to slide ${i + 1}`}
      />
    ))}
  </div>
);

// ── Main Component ───────────────────────────

const OnboardingSlideshow = ({ onClose, onGetStarted }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  const slide = SLIDES[currentSlide];
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === SLIDES.length - 1;

  const goTo = useCallback(
    index => {
      if (index === currentSlide || index < 0 || index >= SLIDES.length) return;
      setCurrentSlide(index);
    },
    [currentSlide]
  );

  const next = () => goTo(currentSlide + 1);
  const prev = () => goTo(currentSlide - 1);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      if (typeof onClose === 'function') onClose();
    }, 300);
  }, [onClose]);

  const handleGetStarted = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      if (typeof onGetStarted === 'function') onGetStarted();
      else if (typeof onClose === 'function') onClose();
    }, 300);
  }, [onGetStarted, onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = e => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        if (!isLast) next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (!isFirst) prev();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentSlide, isFirst, isLast, handleClose]);

  return (
    <div
      className={`${css.overlay} ${isClosing ? css.overlayClosing : ''}`}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Coach onboarding"
    >
      <div className={`${css.modal} ${isClosing ? css.modalClosing : ''}`} onClick={e => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className={css.header}>
          <div className={css.headerGlow} />

          <button
            className={css.closeBtn}
            onClick={handleClose}
            aria-label="Close onboarding"
          >
            &#x2715;
          </button>

          <div className={css.headerContent}>
            <div className={css.iconWrap}>
              <SlideIcon type={slide.icon} />
            </div>

            <span className={`${css.badge} ${slide.id === 'welcome' ? css.badgeGold : ''}`}>
              {slide.badge}
            </span>

            <h2 className={css.title}>{slide.title}</h2>

            <p className={css.subtitle}>
              {slide.id === 'welcome' ? (
                <>
                  by <span className={css.wordmark}>Viveka</span>
                  <span className={css.goldDot}>.</span>
                </>
              ) : (
                slide.subtitle
              )}
            </p>
          </div>

          {/* Progress bar */}
          {slide.progress !== null && (
            <div className={css.progressWrap}>
              <div className={css.progressLabels}>
                <span className={css.progressLabel}>Setup Progress</span>
                <span className={css.progressValue}>{slide.progress}%</span>
              </div>
              <div className={css.progressTrack}>
                <div
                  className={css.progressFill}
                  style={{ width: `${slide.progress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Body ── */}
        <div className={css.body}>
          <p className={css.bodyText}>{slide.body}</p>
          {slide.tip && (
            <div className={css.tipBox}>
              <p className={css.tipText}>{slide.tip}</p>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className={css.footer}>
          <DotIndicators total={SLIDES.length} current={currentSlide} onDotClick={goTo} />

          <div className={css.navRow}>
            {isFirst ? (
              <button className={css.skipBtn} onClick={handleClose}>
                Skip for now
              </button>
            ) : (
              <button className={css.secondaryBtn} onClick={prev}>
                ← Back
              </button>
            )}

            {isLast ? (
              <button className={css.goldBtn} onClick={handleGetStarted}>
                Get Started →
              </button>
            ) : (
              <button className={css.primaryBtn} onClick={next}>
                {isFirst ? "Let's Go" : 'Next'} →
              </button>
            )}
          </div>
        </div>

        {/* ── Branding ── */}
        <div className={css.branding}>
          <span className={css.brandingText}>
            Viveka<span className={css.brandingDot}>.</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OnboardingSlideshow;
